import * as PaymentInvoiceActions from './payment-invoice.actions';

import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {PaymentInvoice, PaymentInvoiceSearchParams, PaymentInvoiceType} from '@accounting/_common/models/payment-invoice.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';
import {BindDocument, UnbindDocument} from '@documents/organization-document/_store/organization-document.actions';

export const paymentInvoicesFeatureKey = 'paymentInvoices';

export function sortByDate(a: PaymentInvoice, b: PaymentInvoice) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<PaymentInvoice> = createEntityAdapter<PaymentInvoice>({
    sortComparer: sortByDate
});


export const typedListInitialState: EntityStateModel<PaymentInvoice, PaymentInvoiceSearchParams> = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export interface PaymentInvoicesState {
    CUSTOMER: EntityStateModel<PaymentInvoice, PaymentInvoiceSearchParams>,
    PROVIDER: EntityStateModel<PaymentInvoice, PaymentInvoiceSearchParams>,
    CROSS: EntityStateModel<PaymentInvoice, PaymentInvoiceSearchParams>
}

export const initialState: PaymentInvoicesState = {
    CUSTOMER: {...typedListInitialState},
    PROVIDER: {...typedListInitialState},
    CROSS: {...typedListInitialState},
};

export const paymentInvoicesReducer = createReducer(
    initialState,

    on(PaymentInvoiceActions.CheckState, (state, action) => {
        if (action.payload.documentType == 'CROSS') {
            if (action.payload.close) {
                return {
                    ...state,
                    CROSS: initialState['CROSS']
                };
            }
            return {...state};
        }

        if (action.payload.modalView) {
            return ({
                ...state,
                [action.payload.documentType]: adapter.removeAll({
                    ...state[action.payload.documentType],
                    savedFilter: !action.payload.close ? state[action.payload.documentType].filter : undefined,
                    pagination: state[action.payload.documentType].pagination,
                    filter: action.payload.close ? state[action.payload.documentType].savedFilter : undefined
                })
            });
        } else {
            if (action.payload.close) {
                return ({
                    ...state,
                    [action.payload.documentType]: adapter.setAll(action.payload.items, {
                        ...state[action.payload.documentType],
                        pagination: {...state[action.payload.documentType].pagination, number: 0},
                        componentOpened: false
                    })
                });
            }

            return {...state, [action.payload.documentType]: {...state[action.payload.documentType], componentOpened: true}};
        }
    }),

    on(PaymentInvoiceActions.LoadList, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: {
                ...state[action.payload.documentType],
                listLoading: StoredComponentHelperService.updateListLoading(action.payload, state[action.payload.documentType].ids)
            }
        });
    }),

    on(PaymentInvoiceActions.LoadListSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: action.payload.clearState
                ? adapter.setAll(action.payload.items, {
                    ...state[action.payload.documentType],
                    pagination: action.payload.pagination,
                    listLoading: undefined

                })
                : adapter.upsertMany(action.payload.items, {
                    ...state[action.payload.documentType],
                    pagination: action.payload.pagination,
                    listLoading: undefined
                }),
        });
    }),

    on(PaymentInvoiceActions.LoadListFailure, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            listLoading: undefined
        }
    })),

    on(PaymentInvoiceActions.SetPagination, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            pagination: {
                ...state[action.payload.documentType].pagination,
                number: action.payload.next ? StoredComponentHelperService.setNextPage(state[action.payload.documentType].pagination, state[action.payload.documentType].ids?.length) : 0
            }
        }
    })),

    on(PaymentInvoiceActions.SetFilter, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            filter: action.payload.filter
        }
    })),

    on(PaymentInvoiceActions.SetLoading, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            loading: action.payload.loading
        }
    })),

    on(PaymentInvoiceActions.SetItemsLoading, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.id, action.payload.isStart)
        }
    })),

    on(PaymentInvoiceActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state[action.payload.documentType].filter, action.payload.item);
        if (savedItemAvailableByFilter) {
            return {
                ...state,
                [action.payload.documentType]: adapter.setOne(action.payload.item, {
                    ...state[action.payload.documentType],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state[action.payload.documentType].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids)
                    }
                })
            };
        } else {
            return ({
                ...state,
                [action.payload.documentType]: adapter.removeOne(action.payload.item.id!, {
                    ...state[action.payload.documentType],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state[action.payload.documentType].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids, true)
                    }
                })
            });
        }
    }),

    on(PaymentInvoiceActions.DeleteItem, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, true)
        }
    })),

    on(PaymentInvoiceActions.DeleteItemSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.documentType]: adapter.removeOne(action.payload.item.id!, {
                ...state[action.payload.documentType],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state[action.payload.documentType].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.documentType].pagination, state[action.payload.documentType]?.ids, true)
                }
            })
        });
    }),

    on(PaymentInvoiceActions.DeleteItemFailure, (state, action) => ({
        ...state,
        [action.payload.documentType]: {
            ...state[action.payload.documentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.documentType].itemsLoading, action.payload.item.id!, false)
        }
    })),

    on(BindDocument, (state, action) => {
        if (action.payload.request.documentType !== 'PROVIDER_PAYMENT_INVOICE' &&  action.payload.request.documentType !== 'CUSTOMER_PAYMENT_INVOICE') {
            return state;
        }

        const documentType: PaymentInvoiceType = action.payload.request.documentType === 'PROVIDER_PAYMENT_INVOICE' ? 'PROVIDER' : 'CUSTOMER';
        return {
            ...state,
            [documentType]: adapter.updateOne({
                id: action.payload.request.itemId,
                changes: {
                    attachmentsExists: true
                }
            }, state[documentType])
        };
    }),

    on(UnbindDocument, (state, action) => {
        if (action.payload.request.documentType !== 'PROVIDER_PAYMENT_INVOICE' &&  action.payload.request.documentType !== 'CUSTOMER_PAYMENT_INVOICE') {
            return state;
        }

        const documentType: PaymentInvoiceType = action.payload.request.documentType === 'PROVIDER_PAYMENT_INVOICE' ? 'PROVIDER' : 'CUSTOMER';
        return {
            ...state,
            [documentType]: adapter.updateOne({
                id: action.payload.request.itemId,
                changes: {
                    attachmentsExists: !action.payload.lastDocumentUnbinded
                }
            }, state[documentType])
        };
    }),

    on(PaymentInvoiceActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const selectAllEntities = (type: PaymentInvoiceType | 'CROSS') => adapter.getSelectors((state: PaymentInvoicesState) => state[type]).selectAll;

const isAvailableByFilter = (searchParams: PaymentInvoiceSearchParams | undefined, document: PaymentInvoice): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.paid !== undefined && searchParams.paid !== document.paid) {
        return false;
    }

    if (searchParams.calculation !== undefined && searchParams.calculation !== document.calculation) {
        return false;
    }

    if (searchParams.type && searchParams.type != document.type) {
        return false;
    }

    if (searchParams.company?.id && searchParams.company.id != document.company?.id) {
        return false;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(document.date))) {
        return false;
    }

    if (searchParams.to && dayjs(document.date).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    return !(searchParams.query && !document.comment?.toLowerCase()?.includes(searchParams.query.toLowerCase()) && document.total != searchParams.query);
};
